import { useTranslation } from "@vixi/localization";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import {
  Button,
  cn,
  TextArea,
  PageMainContent,
  FormDescription,
  FormHeading,
  PaginationWithDelete,
} from "@vixi/ui";
import clsx from "clsx";
import { genArray } from "@vixi/utils";

// Constants
const MIN_POSTS = 6;

interface LinkedInContentFormValues {
  posts: { content: string }[];
}

export const postsSchema = z.object({
  posts: z
    .array(
      z.object({
        content: z.string().refine(
          (val) => {
            // Check for at least 10 words
            const wordCount = val.trim().split(/\s+/).length;
            return wordCount >= 10 || val.trim().length >= 150;
          },
          {
            message: "Each post must have at least 10 words or 150 characters.",
          },
        ),
      }),
    )
    .min(MIN_POSTS, { message: `You must provide at least ${MIN_POSTS} posts.` }),
});

export const LinkedInContentForm = () => {
  const { t } = useTranslation();

  const form = useForm<LinkedInContentFormValues>({
    defaultValues: {
      posts: genArray(() => ({ content: "" }), MIN_POSTS),
    },
    resolver: zodResolver(postsSchema),
  });
  const {
    handleSubmit,
    register,
    control,
    formState: { isValid },
  } = form;

  const posts = useFieldArray({
    control,
    name: "posts",
  });
  const [postIndex, setPostIndex] = useState(0);

  const onSubmit = async (_data: LinkedInContentFormValues) => {
    // Handle form submission logic here
  };

  return (
    <PageMainContent ps="medium" className="flex flex-col  items-center">
      <FormHeading heading={"LinkedInContentForm.Title"} showDivider={false}>
        <FormDescription className="mt-2.5">{t("LinkedInContentForm.Subtitle")}</FormDescription>
      </FormHeading>

      {/* Text field for post content */}
      <div className="w-170.75">
        {posts.fields.map((field, index) => (
          <TextArea
            key={field.id}
            className={clsx("w-full", { hidden: index !== postIndex })}
            rows={16}
            {...register(`posts.${index}.content`, {
              shouldUnregister: false,
            })}
          />
        ))}

        {/* Post navigation */}
        <div className="mt-5">
          <PaginationWithDelete
            visiblePages={11}
            activePage={postIndex}
            totalPages={posts.fields.length}
            onGoToPage={(page) => setPostIndex(page)}
            onDeleteItem={(page) => {
              posts.remove(page);
              if (postIndex >= posts.fields.length - 1) {
                setPostIndex(posts.fields.length - 2);
              }
            }}
            deleteDisabled={posts.fields.length <= 6}
          />
        </div>

        <div className={cn("mx-auto w-fit")}>
          <Button
            variant="outlined"
            size="pill"
            onClick={() => {
              posts.insert(postIndex + 1, { content: "" });
              setPostIndex((prev) => prev + 1);
            }}
          >
            {t("LinkedInContentForm.AddPost")}
          </Button>
        </div>
      </div>

      {isValid && (
        <div className="fixed end-22.5 bottom-12.5">
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleSubmit(onSubmit)}
          >
            {t("Action.Next")}
          </Button>
        </div>
      )}
    </PageMainContent>
  );
};
